"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import SafeHtml from "@/components/ui/SafeHtml";
import { Button } from "@/components/ui/Button";
import { toAbsoluteUrl } from "@/lib/toAbsoluteUrl";
import type { ImageCardFields } from "@/types/acf";

type ACFImage = {
  url?: string;
  alt?: string;
  width?: number;
  height?: number;
};

type WithMobileBg = {
  background_image_mobile?: ACFImage | null;
};

type WithTitleImage = {
  title_image?: ACFImage | null;
};

export default function ImageCard({
  data,
  inColumns = false,
}: {
  data: ImageCardFields;
  inColumns?: boolean;
}) {
  if (!data || data.hide) return null;

  // backgrounds
  const bg =
    data.background_image && typeof data.background_image === "object"
      ? (data.background_image as ACFImage)
      : null;

  const dataWithMobile = data as ImageCardFields & WithMobileBg;

  const bgMobile =
    dataWithMobile.background_image_mobile &&
      typeof dataWithMobile.background_image_mobile === "object"
      ? dataWithMobile.background_image_mobile
      : null;

  const bgDesktopSrc = bg?.url ? toAbsoluteUrl(bg.url) : null;
  const bgMobileSrc = bgMobile?.url ? toAbsoluteUrl(bgMobile.url) : null;

  const title = data.title ?? "";
  const text = data.text ?? "";

  // title image (prefer title_image, fallback to logo for backward compatibility)
  const dataWithTitleImage = data as ImageCardFields & WithTitleImage;

  const titleImage =
    dataWithTitleImage.title_image &&
      typeof dataWithTitleImage.title_image === "object" &&
      (dataWithTitleImage.title_image as ACFImage).url
      ? (dataWithTitleImage.title_image as ACFImage)
      : data.logo &&
        typeof data.logo === "object" &&
        (data.logo as ACFImage).url
        ? (data.logo as ACFImage)
        : null;

  const titleImageSrc = titleImage?.url ? toAbsoluteUrl(titleImage.url) : null;

  // button link
  const button =
    data.button && typeof data.button === "object" && "url" in data.button && data.button.url
      ? data.button
      : null;

  const cardHref = button?.url ? button.url : null;
  const cardAbsHref = cardHref ? toAbsoluteUrl(cardHref) ?? cardHref : null;

  const frameClass = inColumns
    ? "aspect-square min-h-[220px]"
    : "aspect-square md:aspect-auto md:h-[300px]";

  const titleColor =
    (data.theme ?? "").toLowerCase() === "red" ? "text-fuji-red" : "text-white";

  if (!bgDesktopSrc && !bgMobileSrc && !title && !text && !button && !titleImageSrc)
    return null;

  return (
    <div id={data.id} className="min-w-0 w-full">
      <article className={["relative w-full overflow-hidden", frameClass].join(" ")}>
        {cardAbsHref ? (
          <Link
            href={cardAbsHref}
            target={button?.target || undefined}
            rel={button?.target === "_blank" ? "noopener noreferrer" : undefined}
            aria-label={title || button?.title || "Open link"}
            className="absolute inset-0 z-0"
          >
            <span className="sr-only">{title || button?.title || "Open link"}</span>
          </Link>
        ) : null}

        {(bgDesktopSrc || bgMobileSrc) ? (
          <div className="relative w-full h-full">
            {bgMobileSrc ? (
              <Image
                src={bgMobileSrc}
                alt={bgMobile?.alt || ""}
                fill
                sizes="100vw"
                className="object-cover md:hidden"
                priority={false}
              />
            ) : null}

            {bgDesktopSrc ? (
              <Image
                src={bgDesktopSrc}
                alt={bg?.alt || ""}
                fill
                sizes="100vw"
                className={[
                  "object-cover md:object-right",
                  bgMobileSrc ? "hidden md:block" : "block",
                ].join(" ")}
                priority={false}
              />
            ) : null}

            {!bgDesktopSrc && bgMobileSrc ? (
              <Image
                src={bgMobileSrc}
                alt={bgMobile?.alt || ""}
                fill
                sizes="100vw"
                className="object-cover md:object-right hidden md:block"
                priority={false}
              />
            ) : null}
          </div>
        ) : (
          <div className="w-full h-full bg-fuji-black/10" />
        )}

        <div className="absolute inset-x-0 bottom-0 z-10 p-6 md:p-10">
          <div className="flex flex-col items-start gap-3">
            {/* Title image above title, intrinsic size, max 80% width */}
            {titleImageSrc ? (
              <Image
                src={titleImageSrc}
                alt={titleImage?.alt || ""}
                width={titleImage?.width || 200}
                height={titleImage?.height || 80}
                quality={96}
                className="h-auto w-auto object-contain max-w-[180px]"
              />
            ) : null}

            {title ? (
              <h3
                className={[
                  "m-0 text-title-1 max-w-[65%] md:max-w-none",
                  titleColor,
                ].join(" ")}
              >
                <SafeHtml content={title} />
              </h3>
            ) : null}

            {text ? (
              <div className="body-text text-white/90">
                <SafeHtml content={text} />
              </div>
            ) : null}

            {button ? (
              <div className="pt-1 relative z-20">
                <Button
                  as="link"
                  href={toAbsoluteUrl(button.url) ?? "#"}
                  target={button.target || "_self"}
                  rel={button.target === "_blank" ? "noopener noreferrer" : undefined}
                  bgStyle={
                    (data.theme ?? "").toLowerCase() === "white"
                      ? "bg-transparent text-white border-white hover:bg-white hover:text-black hover:border-white"
                      : ""
                  }
                  arrowStyle={
                    (data.theme ?? "").toLowerCase() === "white"
                      ? "text-white group-hover:text-black"
                      : ""
                  }
                >
                  {button.title || "Learn more"}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </article>
    </div>
  );
}
